//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { CashReconciliationFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCompanyService } from "serviceHooks";
import { useCashReconciliationPermissions } from "components/cashReconciliation";
//#endregion
import { FilterHelper, QueryStateHelper } from "utils/helpers";

function useCashReconciliationReportFilterDefinition() {
  const { canViewCashReconciliations } = useCashReconciliationPermissions();
  const companyService = useCompanyService();
  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.CashReconciliationFilter) => {
    const reportFilter: CashReconciliationFormTypes.Filter = {};

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.CashReconciliation.Filter.SaleAmount,
      filter.minSalesAmount,
      filter.maxSalesAmount
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.CashReconciliation.Filter.DifferenceAmount,
      filter.minDifference,
      filter.maxDifference
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.CashReconciliation.Filter.Date,
      filter.fromDate,
      filter.toDate
    );

    let registeredByName: Promise<string> | undefined;
    if (filter.ownerIds?.length) {
      const ownerId = filter.ownerIds[0];
      reportFilter.registeredById = ownerId;
      registeredByName = companyService.getEmployeeName(ownerId);
    }

    if (filter.dimensionIds !== undefined) {
      reportFilter.dimensions = filter.dimensionIds;
    }

    if (registeredByName) {
      reportFilter.registeredBy = await registeredByName;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<CashReconciliationFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        date: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        registeredBy: {
          label: TranslationKeys.CashReconciliation.RegisteredBy,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            referenceFieldName: FormFieldConstants.CashReconciliation.Filter.RegisteredById,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<CashReconciliationFormTypes.Filter>,
          hidden: !canViewCashReconciliations(),
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        differenceAmount: {
          label: TranslationKeys.CashReconciliation.Difference,
          type: FormEnums.FormFieldType.NUMBERRANGE,
          extraProps: {
            allowNegative: true,
            decimalPoints: 2,
          },
        },
        saleAmount: {
          label: TranslationKeys.CashReconciliation.Sale,
          type: FormEnums.FormFieldType.NUMBERRANGE,
          extraProps: {
            allowNegative: true,
            decimalPoints: 2,
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        dimensions: {
          label: TranslationKeys.Common.Tags,
          type: FormEnums.FormFieldType.DIMENSIONINPUTSELECTOR,
          extraProps: {
            type: DimensionEnums.DimensionCategory.CashReconciliation,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<CashReconciliationFormTypes.Filter> = {
    saleAmount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.CashReconciliation.Filter.SaleAmount),
    differenceAmount: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.CashReconciliation.Filter.DifferenceAmount
    ),
    date: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.CashReconciliation.Filter.Date),
    registeredBy: QueryStateHelper.stringQueryEncoder(FormFieldConstants.CashReconciliation.Filter.RegisteredBy),
    registeredById: QueryStateHelper.numberQueryEncoder(FormFieldConstants.CashReconciliation.Filter.RegisteredById),
    dimensions: QueryStateHelper.dimensionsQueryEncoder(FormFieldConstants.CashReconciliation.Filter.Dimensions),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useCashReconciliationReportFilterDefinition;
